import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { GetApp } from "../../App"
import { AppData } from "../../App/AppData"
import { GetShip, GetShipContext } from "../../App/Context"
import { useSocket, useSubscription } from "../../App/useSubscription"
import { logger } from "../../App/Logger"
import { deps, many, useDataLiveEvent, useDataStore, useDeps } from "../../Data"
import {
  IAccountSubscriptionStatus, ICruise, ICurrentDayResponse, IDay, ILog, Log, IStatus, ITrip,
} from "../../Types"
import { Subscription } from "../../Types/Generated/LogbookApi/Subscriptions"
import { FocusViewData } from "./Context"

export interface FocusFrameData {
  cruise: ICruise;
  day: number;
  firstDay: boolean;
  days: IDay[];
  provisionalLogs: Record<string, ILog>;
  subscriptionStatus?: IAccountSubscriptionStatus;
  trip?: ITrip;
}

// Only selection and lifecycle metadata are local. Every entity comes from the
// retained connections, including calculation and nested-note projections.
interface FocusSelection {
  shipId: number;
  cruiseId: number;
  tripId?: number;
  dayIds: number[];
  day: number;
  firstDay: boolean;
  subscriptionStatus?: IAccountSubscriptionStatus;
}

export interface FocusFrameSink {
  beginLoad: () => {
    apply: (response: ICurrentDayResponse, provisionalLogs: ILog[]) => void;
    dispose: () => void;
  };
  seedFromInit: (seed: {
    cruise: ICruise; trip: ITrip; day: IDay;
    logs: ILog[]; statuses: IStatus[]; currentLog: ILog | undefined;
    provisionalLogs: ILog[];
  }) => void;
  clear: () => void;
}

export interface FocusFrameInit {
  initCruise?: ICruise;
  initTrip?: ITrip;
  initDay?: IDay;
  initLogs?: ILog[];
  initStatuses?: IStatus[];
  initCurrentLog?: ILog;
}

export interface FocusFrameResult {
  frame: FocusFrameData | undefined;
  editable: boolean;
  reset: () => void;
}

const CruiseDeps = deps("Ship", { cruises: many("Cruise", { with: ["calculated", "calculatedV2", "notes"] }) });
const TripDeps = deps("Cruise", { trips: many("Trip", { with: ["calculated", "calculatedV2", "notes"] }) });
const DayDeps = deps("Trip", { days: many("Day", { with: ["calculated", "calculatedV2", "notes"] }) });

export function useFocusFrame(init: FocusFrameInit, sink: FocusFrameSink): FocusFrameResult {
  const app = GetApp()!;
  const { requireUserLevel } = GetShipContext();
  const ship = GetShip();
  const store = useDataStore();
  const [selection, setSelection] = useState<FocusSelection>();
  const [loadRevision, setLoadRevision] = useState(0);
  const [provisionalLogs, setProvisionalLogs] = useState<Record<string, ILog>>({});
  const editable = app.editable && requireUserLevel("editCurrent");
  const sinkRef = useRef(sink);
  sinkRef.current = sink;

  // Retain before the bootstrap settles. Unknown owners use an impossible id rather
  // than an unscoped query; fetch:false still retains the entity and its fold sources.
  const { cruises } = useDeps(CruiseDeps, { shipId: ship.id }, { fetch: false });
  const { trips } = useDeps(TripDeps, { shipId: ship.id, cruiseId: selection?.cruiseId ?? -1 }, { fetch: false });
  const { days } = useDeps(DayDeps, { shipId: ship.id, tripId: selection?.tripId ?? -1 }, { fetch: false });
  const cruise = cruises.find((row) => row.id === selection?.cruiseId && row.shipId === ship.id);
  const trip = trips.find((row) => row.id === selection?.tripId && row.shipId === ship.id);
  const frame = useMemo<FocusFrameData | undefined>(() => {
    if (!selection || selection.shipId !== ship.id || !cruise) return undefined;
    return {
      cruise, trip, day: selection.day, firstDay: selection.firstDay,
      days: days.filter((row) => row.shipId === ship.id && selection.dayIds.includes(row.id)),
      provisionalLogs, subscriptionStatus: selection.subscriptionStatus,
    };
  }, [selection, ship.id, cruise, trip, days, provisionalLogs]);

  const provisionalRef = useRef<Record<string, ILog>>({});
  const provisionalRevision = useRef(0);
  const provisionalRevisions = useRef<Record<string, number>>({});
  const loadGeneration = useRef(0);
  const lastRequestedDay = useRef<number | undefined>(undefined);
  const wasDisconnected = useRef(false);
  const pendingLoad = useRef(false);
  const disposeLoad = useRef<(() => void) | undefined>(undefined);
  // Admission events received during the HTTP request must survive its older membership.
  const liveDays = useRef(new Map<number, { row: IDay; deleted: boolean }>());
  const { initCruise, initTrip, initDay, initLogs, initStatuses, initCurrentLog } = init;
  const initMode = Boolean(initCruise && initTrip && initDay);

  const reload = useCallback((resetStatus: boolean) => {
    disposeLoad.current?.();
    disposeLoad.current = undefined;
    loadGeneration.current += 1;
    lastRequestedDay.current = undefined;
    pendingLoad.current = false;
    provisionalRef.current = {};
    setProvisionalLogs({});
    setSelection(undefined);
    sinkRef.current.clear();
    setLoadRevision((previous) => previous + 1);
    if (resetStatus) app.resetStatus();
  }, [app]);
  const reset = useCallback(() => reload(true), [reload]);
  const previousStore = useRef(store);

  // Provider transport changes can replace the store without remounting Focus.
  // Selection alone does not mean that the new store has been bootstrapped.
  useEffect(() => {
    if (previousStore.current !== store) {
      previousStore.current = store;
      if (!initMode) reload(false);
    }
  }, [store, reload, initMode]);

  useSocket((connected) => {
    if (!connected) {
      wasDisconnected.current = true;
    } else if (wasDisconnected.current && !initMode) {
      wasDisconnected.current = false;
      reload(false);
    }
  });

  useEffect(() => {
    if (initCruise && initTrip && initDay) {
      sinkRef.current.seedFromInit({
        cruise: initCruise, trip: initTrip, day: initDay,
        logs: initLogs ?? [], statuses: initStatuses ?? [], currentLog: initCurrentLog,
        provisionalLogs: Object.values(provisionalRef.current),
      });
      setSelection({
        shipId: ship.id, cruiseId: initCruise.id, tripId: initTrip.id, dayIds: [initDay.id],
        day: initDay.day, firstDay: true, subscriptionStatus: IAccountSubscriptionStatus.inactive,
      });
    }
    // Init props are bootstrap data, not a second source that overwrites live rows.
  }, [store, ship.id, initCruise?.id, initTrip?.id, initDay?.id, loadRevision]);

  useEffect(() => {
    if (initMode || pendingLoad.current) return;
    const now = new Date();
    const clockState = selection && selection.shipId === ship.id
      ? { day: selection.day, trip }
      : undefined;
    if (!FocusViewData.shouldLoadCurrentDay(clockState, now, app.status?.systemTimezone ?? 0, lastRequestedDay.current)) return;
    pendingLoad.current = true;
    const generation = ++loadGeneration.current;
    const snapshotRevision = provisionalRevision.current;
    liveDays.current = new Map();
    const loadSeed = sinkRef.current.beginLoad();
    disposeLoad.current = loadSeed.dispose;
    const load = async () => {
      try {
        const current = await FocusViewData.loadCurrentDay(app.apiClient);
        if (generation !== loadGeneration.current) return;
        const responseCruise = current.cruises.cruises.toSorted((a, b) => a.id - b.id)[0];
        const responseTrip = current.trips?.trips.toSorted((a, b) => a.id - b.id)[0];
        const responseTimezone = responseTrip?.timezone ?? app.status?.systemTimezone ?? 0;
        lastRequestedDay.current = FocusViewData.todayDayNumber(now, responseTimezone);
        const snapshot: Record<string, ILog> = {};
        for (const log of current.provisionalLogs ?? []) {
          const key = Log.parse(log).dataParsed.provisionalKey;
          if (key) snapshot[key] = log;
        }
        const reconciled = { ...provisionalRef.current };
        for (const key of new Set([...Object.keys(reconciled), ...Object.keys(snapshot)])) {
          if ((provisionalRevisions.current[key] ?? 0) > snapshotRevision) continue;
          if (snapshot[key]) reconciled[key] = snapshot[key];
          else delete reconciled[key];
        }
        provisionalRef.current = reconciled;
        loadSeed.apply(current, Object.values(reconciled));
        setProvisionalLogs(reconciled);
        const dayIds = new Set(current.days?.days.map((day) => day.id) ?? []);
        for (const { row, deleted } of liveDays.current.values()) {
          if (deleted) dayIds.delete(row.id);
          else if (row.shipId === ship.id && row.tripId === responseTrip?.id && (row.day >= current.day - 1 || row.day === 0)) dayIds.add(row.id);
        }
        setSelection(responseCruise ? {
          shipId: ship.id, cruiseId: responseCruise.id, tripId: responseTrip?.id, dayIds: [...dayIds],
          day: current.day, firstDay: current.firstDay < 1 || current.day === current.firstDay,
          subscriptionStatus: current.subscriptionStatus,
        } : undefined);
      } finally {
        loadSeed.dispose();
        if (generation === loadGeneration.current) {
          pendingLoad.current = false;
          disposeLoad.current = undefined;
        }
      }
    };
    load().catch((error) => logger.error("load current day", error));
  }, [frame, selection, trip, initMode, loadRevision, ship.id, app.status?.systemTimezone]);

  // Invalidate in-flight work when changing ships or unmounting, including its seed.
  useEffect(() => () => {
    disposeLoad.current?.();
    disposeLoad.current = undefined;
    loadGeneration.current += 1;
    pendingLoad.current = false;
    lastRequestedDay.current = undefined;
  }, [ship.id, store]);

  useSubscription((log: ILog, _type: string, info: string) => {
    const key = Log.parse(log).dataParsed.provisionalKey;
    if (key) {
      provisionalRevision.current += 1;
      provisionalRevisions.current[key] = provisionalRevision.current;
    }
    provisionalRef.current = AppData.updateProvisional(provisionalRef.current, key, log, info);
    setProvisionalLogs(provisionalRef.current);
  }, Subscription.provisionalLog);

  useDataLiveEvent(store, "Cruise", (row, info) => {
    if (row.shipId === ship.id && info === "create" && row.id !== selection?.cruiseId && !initMode) reset();
  });
  useDataLiveEvent(store, "Trip", (row, info) => {
    if (row.shipId === ship.id && info === "create" && row.id !== selection?.tripId && !initMode) reset();
  });
  useDataLiveEvent(store, "Day", (row, info) => {
    const deleted = info === "delete" || store.getEntity("Day", row.id) === undefined;
    if (pendingLoad.current) liveDays.current.set(row.id, { row, deleted });
    setSelection((previous) => {
      if (!previous) return previous;
      if (deleted) return { ...previous, dayIds: previous.dayIds.filter((id) => id !== row.id) };
      if (row.shipId !== ship.id || row.tripId !== previous.tripId || (row.day < previous.day - 1 && row.day !== 0) || previous.dayIds.includes(row.id)) return previous;
      return { ...previous, dayIds: [...previous.dayIds, row.id] };
    });
  });
  useSubscription((day: IDay) => {
    if (selection && selection.day !== day.day) reset();
  }, Subscription.dayNavigation);

  return { frame, editable, reset };
}
